import React, { useState } from 'react';

// Define types for our component
interface CsvToGraphQLProps {}

// CSV parsing function
const parseCSV = (
  csv: string,
  options: {
    delimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
  }
): string[][] => {
  try {
    // Split the CSV by lines
    const lines = csv.split(/\r?\n/);
    
    // Handle empty CSV
    if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === '')) {
      return [];
    }
    
    // Process all rows
    const rows: string[][] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines if configured to do so
      if (options.skipEmptyLines && line === '') {
        continue;
      }
      
      const values = parseCSVLine(line, options.delimiter);
      
      if (values.length > 0) {
        if (options.trimValues) {
          rows.push(values.map(v => v.trim()));
        } else {
          rows.push(values);
        }
      }
    }
    
    return rows;
  } catch (error) {
    throw error;
  }
};

// Helper function to parse a CSV line respecting quoted values
const parseCSVLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Handle escaped double quotes
      if (i + 1 < line.length && line[i + 1] === '"') {
        currentValue += '"';
        i++; // Skip the next quote
      } else {
        // Toggle the quote state
        insideQuotes = !insideQuotes;
      }
    } else if (char === delimiter && !insideQuotes) {
      // End of field
      values.push(currentValue);
      currentValue = '';
    } else {
      // Regular character
      currentValue += char;
    }
  }
  
  // Add the last value
  values.push(currentValue);
  
  return values;
};

// Function to infer GraphQL type from a value
const inferGraphQLType = (value: string): string => {
  // Check if value is empty
  if (value === null || value === undefined || value.trim() === '') {
    return 'String';
  }
  
  // Check if value is a number
  if (!isNaN(Number(value)) && value.trim() !== '') {
    // Check if it's an integer
    if (Number.isInteger(Number(value))) {
      return 'Int';
    }
    return 'Float';
  }
  
  // Check if value is a boolean
  if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
    return 'Boolean';
  }
  
  // Check if value is a date
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return 'Date';
  }
  
  // Default to String
  return 'String';
};

// Function to convert a string to a valid GraphQL type name
const toValidTypeName = (str: string): string => {
  // Remove all non-alphanumeric characters and convert to PascalCase
  return str
    .replace(/[^a-zA-Z0-9]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
};

// Function to convert a string to a valid field name (camelCase)
const toValidFieldName = (str: string): string => {
  // Convert to camelCase
  const pascalCase = toValidTypeName(str);
  return pascalCase.charAt(0).toLowerCase() + pascalCase.slice(1);
};

// CSV to GraphQL conversion function
const csvToGraphQL = (
  csv: string,
  options: {
    typeName: string;
    delimiter: string;
    trimValues: boolean;
    skipEmptyLines: boolean;
    includeQueries: boolean;
    includeMutations: boolean;
    includeInputTypes: boolean;
    includeDateScalar: boolean;
  }
): string => {
  try {
    // Parse the CSV data
    const parsedData = parseCSV(csv, {
      delimiter: options.delimiter,
      trimValues: options.trimValues,
      skipEmptyLines: options.skipEmptyLines
    });
    
    // Handle empty data
    if (parsedData.length === 0) {
      return 'No data to convert';
    }
    
    // Extract headers and data
    const headers = parsedData[0];
    const data = parsedData.slice(1);
    
    // Infer types based on data
    const fieldTypes: { [key: string]: string } = {};
    
    // First, initialize all field types to String
    headers.forEach(header => {
      fieldTypes[header] = 'String';
    });
    
    // Then, analyze the data to infer better types
    data.forEach(row => {
      headers.forEach((header, index) => {
        const value = row[index];
        const inferredType = inferGraphQLType(value);
        
        // Only "upgrade" type if we find a non-String and current type is String
        if (fieldTypes[header] === 'String' && inferredType !== 'String') {
          fieldTypes[header] = inferredType;
        }
        // If we previously inferred Int but now find Float, upgrade to Float
        else if (fieldTypes[header] === 'Int' && inferredType === 'Float') {
          fieldTypes[header] = 'Float';
        }
        // If we have conflicting types, default to String
        else if (fieldTypes[header] !== 'String' && 
                 inferredType !== 'String' && 
                 fieldTypes[header] !== inferredType) {
          fieldTypes[header] = 'String';
        }
      });
    });
    
    // Convert CSV headers to GraphQL type
    let graphqlSchema = '';
    const typeName = toValidTypeName(options.typeName);
    
    // Check if we need to include the Date scalar
    const usesDateType = Object.values(fieldTypes).includes('Date');
    if (usesDateType && options.includeDateScalar) {
      graphqlSchema += '# Define custom Date scalar\n';
      graphqlSchema += 'scalar Date\n\n';
    }
    
    // Build the main type
    graphqlSchema += `type ${typeName} {\n`;
    graphqlSchema += '  id: ID!\n';
    
    headers.forEach(header => {
      const fieldName = toValidFieldName(header);
      const fieldType = fieldTypes[header];
      graphqlSchema += `  ${fieldName}: ${fieldType}\n`;
    });
    
    graphqlSchema += '}\n\n';
    
    // Build Input type if requested
    if (options.includeInputTypes) {
      graphqlSchema += `input ${typeName}Input {\n`;
      
      headers.forEach(header => {
        const fieldName = toValidFieldName(header);
        const fieldType = fieldTypes[header];
        graphqlSchema += `  ${fieldName}: ${fieldType}\n`;
      });
      
      graphqlSchema += '}\n\n';
    }
    
    // Build Query type if requested
    if (options.includeQueries) {
      const pluralTypeName = `${typeName}s`;
      
      graphqlSchema += 'type Query {\n';
      graphqlSchema += `  ${typeName.toLowerCase()}(id: ID!): ${typeName}\n`;
      graphqlSchema += `  ${pluralTypeName.toLowerCase()}: [${typeName}!]!\n`;
      graphqlSchema += '}\n\n';
    }
    
    // Build Mutation type if requested
    if (options.includeMutations) {
      graphqlSchema += 'type Mutation {\n';
      graphqlSchema += `  create${typeName}(input: ${typeName}Input!): ${typeName}!\n`;
      graphqlSchema += `  update${typeName}(id: ID!, input: ${typeName}Input!): ${typeName}!\n`;
      graphqlSchema += `  delete${typeName}(id: ID!): Boolean!\n`;
      graphqlSchema += '}\n\n';
    }
    
    // Build Schema definition
    const hasQueryOrMutation = options.includeQueries || options.includeMutations;
    if (hasQueryOrMutation) {
      graphqlSchema += 'schema {\n';
      if (options.includeQueries) {
        graphqlSchema += '  query: Query\n';
      }
      if (options.includeMutations) {
        graphqlSchema += '  mutation: Mutation\n';
      }
      graphqlSchema += '}\n';
    }
    
    return graphqlSchema;
  } catch (error) {
    throw error;
  }
};

// Main component to convert CSV to GraphQL
const CsvToGraphQL: React.FC<CsvToGraphQLProps> = () => {
  // State for the CSV input
  const [csvInput, setCsvInput] = useState<string>('');
  // State for the GraphQL output
  const [graphqlOutput, setGraphqlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the type name
  const [typeName, setTypeName] = useState<string>('Product');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isGraphQLGenerated, setIsGraphQLGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  
  // Conversion options
  const [delimiter, setDelimiter] = useState<string>(',');
  const [trimValues, setTrimValues] = useState<boolean>(true);
  const [skipEmptyLines, setSkipEmptyLines] = useState<boolean>(true);
  const [includeQueries, setIncludeQueries] = useState<boolean>(true);
  const [includeMutations, setIncludeMutations] = useState<boolean>(true);
  const [includeInputTypes, setIncludeInputTypes] = useState<boolean>(true);
  const [includeDateScalar, setIncludeDateScalar] = useState<boolean>(true);
  
  // Handle CSV input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCsvInput(e.target.value);
    setError('');
    setIsGraphQLGenerated(false);
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Infer type name from file name
    const fileName = file.name.split('.')[0];
    setTypeName(toValidTypeName(fileName));
    
    // Read the file content
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const csv = event.target.result as string;
        setCsvInput(csv);
        setError('');
        setIsGraphQLGenerated(false);
      }
    };
    reader.onerror = () => {
      setError('Error reading the file');
    };
    reader.readAsText(file);
  };

  // Use example data
  const useExampleData = () => {
    const exampleCsv = `Product,Category,Price,Stock,Rating,IsActive,CreatedAt
Basic T-Shirt,Clothing,19.99,120,4.2,true,2023-01-15
Premium Hoodie,Clothing,49.99,65,4.8,true,2023-02-10
Wireless Earbuds,Electronics,89.99,42,4.5,true,2023-01-20
Smart Watch,Electronics,199.99,30,4.7,false,2023-03-05
Running Shoes,Footwear,79.99,80,4.3,true,2023-02-28
Yoga Mat,Fitness,29.99,55,4.6,true,2023-01-10
Protein Powder,Nutrition,39.99,100,4.4,true,2023-03-15
Water Bottle,Accessories,14.99,150,4.1,true,2023-01-05`;

    setCsvInput(exampleCsv);
    setTypeName('Product');
    setError('');
    setIsGraphQLGenerated(false);
    setGraphqlOutput('');
  };

  // Convert CSV to GraphQL
  const convertCsvToGraphQL = () => {
    setError('');
    setIsLoading(true);
    setIsGraphQLGenerated(false);
    setGraphqlOutput('');
    
    if (!csvInput.trim()) {
      setError('Please enter CSV data');
      setIsLoading(false);
      return;
    }

    try {
      // Generate the GraphQL schema
      const options = {
        typeName,
        delimiter,
        trimValues,
        skipEmptyLines,
        includeQueries,
        includeMutations,
        includeInputTypes,
        includeDateScalar
      };
      
      const graphql = csvToGraphQL(csvInput, options);
      setGraphqlOutput(graphql);
      setIsGraphQLGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error converting CSV to GraphQL: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setCsvInput('');
    setGraphqlOutput('');
    setError('');
    setTypeName('Product');
    setIsGraphQLGenerated(false);
  };

  // Copy GraphQL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(graphqlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download GraphQL file
  const downloadGraphQL = () => {
    const blob = new Blob([graphqlOutput], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${typeName.toLowerCase()}.graphql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">CSV to GraphQL Schema Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert CSV (Comma-Separated Values) data to GraphQL schema definitions. Upload a CSV file or paste your data below.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="csvInput" className="font-medium text-gray-700">
                CSV Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setCsvInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
            </div>
            
            <textarea
              id="csvInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={csvInput}
              onChange={handleInputChange}
              placeholder="Paste your CSV data here, e.g.,
Product,Category,Price,Stock
T-Shirt,Clothing,19.99,120
Headphones,Electronics,89.99,42"
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="typeName" className="block text-sm font-medium text-gray-700 mb-1">
                GraphQL Type Name
              </label>
              <input
                type="text"
                id="typeName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
                placeholder="Enter GraphQL type name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="delimiter" className="block text-sm font-medium text-gray-700 mb-1">
                    Delimiter
                  </label>
                  <select
                    id="delimiter"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="trimValues"
                    checked={trimValues}
                    onChange={(e) => setTrimValues(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="trimValues" className="ml-2 block text-sm text-gray-700">
                    Trim whitespace from values
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="skipEmptyLines"
                    checked={skipEmptyLines}
                    onChange={(e) => setSkipEmptyLines(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="skipEmptyLines" className="ml-2 block text-sm text-gray-700">
                    Skip empty lines
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeQueries"
                    checked={includeQueries}
                    onChange={(e) => setIncludeQueries(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeQueries" className="ml-2 block text-sm text-gray-700">
                    Include Query type
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeMutations"
                    checked={includeMutations}
                    onChange={(e) => setIncludeMutations(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeMutations" className="ml-2 block text-sm text-gray-700">
                    Include Mutation type
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeInputTypes"
                    checked={includeInputTypes}
                    onChange={(e) => setIncludeInputTypes(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeInputTypes" className="ml-2 block text-sm text-gray-700">
                    Include Input types
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includeDateScalar"
                    checked={includeDateScalar}
                    onChange={(e) => setIncludeDateScalar(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includeDateScalar" className="ml-2 block text-sm text-gray-700">
                    Include Date scalar (if date fields are detected)
                  </label>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertCsvToGraphQL}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to GraphQL'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              GraphQL Schema Output
            </label>
            
            {isGraphQLGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy GraphQL'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadGraphQL}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isGraphQLGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    GraphQL schema will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isGraphQLGenerated && (
              <div className="flex-grow flex flex-col">
                <textarea
                  id="graphqlOutput"
                  className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  value={graphqlOutput}
                  readOnly
                />
              </div>
            )}
          </div>
          
          {isGraphQLGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">CSV Successfully Converted to GraphQL Schema!</p>
              </div>
              <p className="text-sm">
                You can now copy or download the generated GraphQL schema for use in your API or GraphQL server.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About CSV to GraphQL Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes CSV (Comma-Separated Values) data and intelligently generates GraphQL schema definitions. It examines header names and data types from your CSV to create appropriate GraphQL types, queries, and mutations. The converter automatically detects data types like strings, numbers, booleans, and dates to create a well-structured GraphQL schema.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Automatic Type Inference</strong> - Detects appropriate GraphQL types from your data</li>
              <li><strong>Query Generation</strong> - Creates query operations for fetching data</li>
              <li><strong>Mutation Generation</strong> - Builds mutation operations for modifying data</li>
              <li><strong>Input Types</strong> - Creates input types for mutations</li>
              <li><strong>Custom Scalar Support</strong> - Adds Date scalar when date fields are detected</li>
              <li><strong>Field Name Optimization</strong> - Converts CSV headers to valid GraphQL field names</li>
              <li><strong>File Upload</strong> - Upload CSV files directly or paste content manually</li>
              <li><strong>Multiple Delimiter Support</strong> - Handle various delimiters (comma, semicolon, tab, pipe)</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">GraphQL Schema Structure</h3>
            <p className="text-gray-600">
              The generated GraphQL schema follows these conventions:
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Common Use Cases</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Rapidly prototyping GraphQL APIs from existing data</li>
              <li>Migrating legacy CSV data to GraphQL-based systems</li>
              <li>Creating schema definitions for GraphQL servers</li>
              <li>Generating type definitions for frontend development</li>
              <li>Building data models for full-stack applications</li>
              <li>Converting spreadsheet data into GraphQL schemas</li>
              <li>Bootstrapping GraphQL projects with real-world data</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Type Inference Logic</h3>
            <p className="text-gray-600">
              The converter uses the following rules to determine GraphQL types:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>String</strong> - Default type for text data and mixed content</li>
              <li><strong>Int</strong> - For whole numbers without decimal points</li>
              <li><strong>Float</strong> - For numbers with decimal points</li>
              <li><strong>Boolean</strong> - For "true" or "false" values (case insensitive)</li>
              <li><strong>Date</strong> - For values matching ISO date format (YYYY-MM-DD)</li>
              <li><strong>ID</strong> - Added automatically as a required field</li>
            </ul>
            <p className="text-gray-600 mt-2">
              When conflicting types are detected in the same column, the converter intelligently chooses the most appropriate type or defaults to String for maximum compatibility.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">CSV Format Tips</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Include descriptive headers that will convert well to GraphQL field names</li>
              <li>Use consistent data types within each column for optimal type inference</li>
              <li>Use ISO format (YYYY-MM-DD) for dates to ensure proper detection</li>
              <li>Use "true" or "false" literals for boolean values</li>
              <li>Use double quotes around values that contain delimiters or newlines</li>
              <li>Keep column count consistent across all rows</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Next Steps After Conversion</h3>
            <ol className="list-decimal pl-5 text-gray-600">
              <li>Save the generated GraphQL schema to a .graphql file</li>
              <li>Import the schema into your GraphQL server implementation</li>
              <li>Implement resolvers for the queries and mutations</li>
              <li>Connect to your data source (database, API, etc.)</li>
              <li>Test your GraphQL API with tools like GraphiQL or Apollo Studio</li>
              <li>Generate TypeScript or other language types from your schema</li>
              <li>Iterate and refine your schema as needed</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvToGraphQL;